import { DistanceUnit, MovementResult, ScoringType, WeightUnit } from 'components/Workout/ScoringTypes';
import { WorkoutRetrieve, WorkoutSectionMovementRetrieve, WorkoutSectionRetrieve } from 'types';

export type { WorkoutResultWithSets } from 'types';

export type {
  ScoringType,
  MovementResult,
  TimeResult,
  CaloriesResult,
  DistanceResult,
  RepsResult,
  RoundsResult,
  RoundsRepsResult,
  WeightResult,
  WeightSetEntry,
  WeightUnit,
  DistanceUnit,
} from 'components/Workout/ScoringTypes';
export { blockNonInteger, blockNonDecimal } from 'components/Workout/ScoringTypes';

export type ResultsState = Record<string, MovementResult>;

export type WorkoutMovementWithScoring = WorkoutSectionMovementRetrieve & {
  setCount?: number;
  scoringType: ScoringType;
};

export type WorkoutSectionWithScoring = Omit<WorkoutSectionRetrieve, 'movements'> & {
  movements: WorkoutMovementWithScoring[];
};

export type EnrichedWorkout = Omit<WorkoutRetrieve, 'sections'> & {
  sections: WorkoutSectionWithScoring[];
};

export const createEmptyMovementResult = (movement: WorkoutMovementWithScoring): MovementResult => {
  const { scoringType } = movement;
  switch (scoringType) {
    case ScoringType.TIME:
      return { scoringType, minutes: '', seconds: '' };
    case ScoringType.CALORIES:
      return { scoringType, calories: '' };
    case ScoringType.DISTANCE: {
      const unit: DistanceUnit =
        movement.distanceUnit === 'km' ? 'km' : movement.distanceUnit === 'mi' ? 'mi' : 'm';
      return { scoringType, value: '', unit };
    }
    case ScoringType.REPS:
      return { scoringType, reps: '' };
    case ScoringType.ROUNDS:
      return { scoringType, rounds: '' };
    case ScoringType.ROUNDS_REPS:
      return { scoringType, rounds: '', reps: '' };
    case ScoringType.WEIGHT: {
      const unit: WeightUnit = movement.weightUnit === 'kg' ? 'kg' : 'lbs';
      const setCount = movement.setCount ?? 0;
      return {
        scoringType,
        sets: Array.from({ length: setCount }, (_, i) => ({
          reps: movement.scoreSets[i]?.targetReps ?? ('' as const),
          weight: '' as const,
          unit,
        })),
      };
    }
  }
};

// Multi-field scoring types (TIME, ROUNDS_REPS, WEIGHT) require at least one
// positive value before we attempt to save — the backend's manager rejects
// all-zero submissions (e.g. "Total time must be greater than zero", "rounds
// and extra_reps cannot both be zero"), and we'd rather guide the athlete with
// a sticky dirty indicator than surface a backend error mid-flow.
export const isMovementResultFilled = (result: MovementResult): boolean => {
  switch (result.scoringType) {
    case ScoringType.TIME:
      return (
        (typeof result.minutes === 'number' && result.minutes > 0) ||
        (typeof result.seconds === 'number' && result.seconds > 0)
      );
    case ScoringType.CALORIES:
      return result.calories !== '';
    case ScoringType.DISTANCE:
      return result.value !== '';
    case ScoringType.REPS:
      return result.reps !== '';
    case ScoringType.ROUNDS:
      return result.rounds !== '';
    case ScoringType.ROUNDS_REPS:
      return (
        (typeof result.rounds === 'number' && result.rounds > 0) ||
        (typeof result.reps === 'number' && result.reps > 0)
      );
    case ScoringType.WEIGHT:
      return result.sets.some(
        s =>
          typeof s.reps === 'number' &&
          s.reps > 0 &&
          typeof s.weight === 'number' &&
          s.weight > 0,
      );
  }
};
